// Entity vs cohort score trend overlay.

import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'

import { Card } from '@/components/cards/Card'
import { fetchingAPI, apiService } from '@/api'

import { CHART } from '@/components/charts/chartPalette'
import { buildDashboardParams, useDashboardOrgId } from '../../helpers/dashboardParams'
import { FILTER_KEY_BY_SCOPE, usePickedEntity } from '../../helpers/cohort'
import { escapeHtml } from '../../helpers/format'
import { ChartWidgetCard } from '../chartWidget'

export const ScoreTrendOverlayWidget = ({ scope }) => {
  // fetchingAPI wants a dispatch for its token-expiry logout path.
  const dispatch = useDispatch()
  // Plain filters (NOT useFiltersForPickedScope): the entity line pins
  // the picker's entity; the cohort line needs the picker dim stripped.
  const filters = useSelector((state) => state.qaCopilot.filters)
  const orgId = useDashboardOrgId()
  const pickedEntity = usePickedEntity(scope)

  const [entityData, setEntityData] = useState(null)
  const [cohortData, setCohortData] = useState(null)
  const [loading, setLoading] = useState(false)

  // Own fetch effect (two parallel requests) -- useWidgetData only
  // covers the single-URL case.
  useEffect(() => {
    if (!orgId || !pickedEntity) {
      return undefined
    }
    let cancelled = false
    setLoading(true)

    const entityParams = buildDashboardParams(filters, orgId)
    entityParams.append(FILTER_KEY_BY_SCOPE[scope], pickedEntity.value)
    const cohortParams = buildDashboardParams(filters, orgId)

    Promise.all([
      fetchingAPI(
        `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/multiple/by_date?${entityParams.toString()}`,
        'GET',
        dispatch
      ),
      fetchingAPI(
        `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/multiple/by_date?${cohortParams.toString()}`,
        'GET',
        dispatch
      ),
    ])
      .then(([entity, cohort]) => {
        if (cancelled) {
          return
        }
        setEntityData(entity)
        setCohortData(cohort)
      })
      .catch(() => {
        if (!cancelled) {
          setEntityData(null)
          setCohortData(null)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [orgId, scope, pickedEntity?.value, filters])

  const option = useMemo(() => {
    // Zip entity + cohort by `row.date`, NOT by index -- index-aligned
    // plotting slides entity points onto the wrong cohort day.
    const scoreFor = (row) => {
      const score = parseFloat(row?.['QA Score'])
      return Number.isFinite(score) ? score : null
    }
    const entityAggregates = entityData?.aggregates || []
    const cohortAggregates = cohortData?.aggregates || []
    const entityByDate = new Map()
    entityAggregates.forEach((row) => {
      const key = row.date || row.day
      if (key) {
        entityByDate.set(key, scoreFor(row))
      }
    })
    const cohortByDate = new Map()
    cohortAggregates.forEach((row) => {
      const key = row.date || row.day
      if (key) {
        cohortByDate.set(key, scoreFor(row))
      }
    })
    // 'YYYY-MM-DD' keys sort fine as strings; the legacy 'MM/DD' shape
    // does NOT ('10/01' sorts before '2/01'). Zero-pad to a stable
    // chronological key instead of relying on the default sort.
    const chronoKey = (key) => {
      const str = String(key)
      if (!str.includes('/')) {
        return str
      }
      const [month, day] = str.split('/')
      return `0000-${month.padStart(2, '0')}-${(day || '').padStart(2, '0')}`
    }
    const dates = Array.from(new Set([...entityByDate.keys(), ...cohortByDate.keys()])).sort(
      (dateA, dateB) => chronoKey(dateA).localeCompare(chronoKey(dateB))
    )
    const entityScores = dates.map((date) =>
      entityByDate.has(date) ? entityByDate.get(date) : null
    )
    const cohortScores = dates.map((date) =>
      cohortByDate.has(date) ? cohortByDate.get(date) : null
    )
    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const day = params[0]?.axisValue || ''
          const selectedLabel = pickedEntity?.label || 'Selected'
          const entity = params.find((row) => row.seriesName === selectedLabel)?.value
          const cohort = params.find((row) => row.seriesName === 'Average')?.value
          const entityText = isNil(entity) ? '-' : `${entity}%`
          const cohortText = isNil(cohort) ? '-' : `${cohort}%`
          return `${day}<br/>${escapeHtml(selectedLabel)}: ${entityText}<br/>Average: ${cohortText}`
        },
      },
      legend: {
        top: 0,
        right: 8,
        textStyle: { fontSize: 11 },
      },
      grid: { left: 40, right: 16, top: 28, bottom: 32 },
      xAxis: { type: 'category', data: dates, axisLabel: { fontSize: 10 } },
      yAxis: { type: 'value', min: 0, max: 100, name: 'QA Score' },
      series: [
        {
          // Average as a solid blue reference line.
          name: 'Average',
          type: 'line',
          data: cohortScores,
          smooth: true,
          showSymbol: false,
          lineStyle: { color: CHART.INFORMATIVE, width: 2 },
          itemStyle: { color: CHART.INFORMATIVE },
        },
        {
          // Selected entity as a solid orange line with visible symbols.
          name: pickedEntity?.label || 'Selected',
          type: 'line',
          data: entityScores,
          smooth: true,
          showSymbol: true,
          symbolSize: 6,
          lineStyle: { color: CHART.CAUTION, width: 2.5 },
          itemStyle: { color: CHART.CAUTION },
        },
      ],
    }
  }, [entityData, cohortData, pickedEntity])

  if (!pickedEntity) {
    return (
      <Card>
        <div className="entity-drawer-empty">Pick an entity above.</div>
      </Card>
    )
  }
  return (
    <ChartWidgetCard
      title={`Score trend -- ${pickedEntity.label || ''} vs average`}
      info="This entity's QA score over time overlaid on the cohort average, so you can see whether it's tracking, pulling ahead, or falling behind."
      option={option}
      loading={loading}
      height="280px"
    />
  )
}
